import { MessagesButton } from "../components/GroupChat";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Button,
  Tooltip,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import PaletteIcon from "@mui/icons-material/Palette";
import CheckIcon from "@mui/icons-material/Check";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

import api from "../api/client";
import { AuthContext } from "../context/AuthContext";
import { ColorModeContext, THEME_MODES } from "../theme/ThemeProvider";
import { useAnnouncement } from "../context/AnnouncementContext";
import { resolvePageMeta } from "../constants/pageMeta";

import IncompleteIncidentsWarning from "../components/IncompleteIncidentsWarning";
import RotatingQuote from "../components/RotatingQuote";
import useElementHeight from "../hooks/useElementHeight";
import WorkspaceTools from "../components/WorkspaceTools";
import {
  DEFAULT_PING_INTERVAL_SECONDS,
  MAX_PING_INTERVAL_SECONDS,
  MIN_PING_INTERVAL_SECONDS,
} from "../constants/systemConfig";
import { updateProfile } from "../utils/profileStorage";

const drawerWidth = 280;
const collapsedDrawerWidth = 0;

const navButtonStyles = {
  mx: 1, mb: 0.5, borderRadius: 1.5,
  "&.active": {
    backgroundColor: "action.selected",
    color: "primary.main",
    "& .MuiListItemText-primary": { color: "primary.main", fontWeight: 750 },
  },
};

const nestedNavStyles = {
  ...navButtonStyles,
  pl: 4,
};

function DashboardLayout({ title, children, toolbarExtras, workspaceToolsProps, sidebarCollapsed: externalSidebarCollapsed, onSidebarToggle, tabletMode = false }) {
  const location = useLocation();
  const [headerRef, headerHeight] = useElementHeight(64);
  const theme = useTheme();
  const compactNavigation = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  useEffect(() => { setMobileNavigationOpen(false); }, [compactNavigation, location.pathname]);
  const pageMeta = useMemo(() => resolvePageMeta(location.pathname), [location.pathname]);
  const showHeaderLogo = Boolean(pageMeta.logo);

  const { user, logout } = useContext(AuthContext);
  const { mode, setColorMode } = useContext(ColorModeContext);
  const { announcement, snowbankEnabled, toggleSnowbank } = useAnnouncement();

  // Theme menu state
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);
  const themeMenuOpen = Boolean(themeMenuAnchor);

  // Sidebar collapse state - use external state if provided, otherwise manage internally
  const [internalSidebarCollapsed, setInternalSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  const sidebarCollapsed = externalSidebarCollapsed !== undefined ? externalSidebarCollapsed : internalSidebarCollapsed;

  const toggleSidebar = useCallback(() => {
    if (compactNavigation) {
      setMobileNavigationOpen(open => !open);
      return;
    }
    if (onSidebarToggle) {
      onSidebarToggle();
    } else {
      setInternalSidebarCollapsed(prev => {
        const newValue = !prev;
        localStorage.setItem('sidebarCollapsed', String(newValue));
        return newValue;
      });
    }
  }, [compactNavigation, onSidebarToggle]);

  const currentDrawerWidth = compactNavigation || sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;
  const navigationOpen = compactNavigation ? mobileNavigationOpen : !sidebarCollapsed;
  const navigationLabel = compactNavigation
    ? navigationOpen ? "Close navigation" : "Open navigation"
    : sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar";

  const handleThemeMenuOpen = (event) => {
    setThemeMenuAnchor(event.currentTarget);
  };

  const handleThemeMenuClose = () => {
    setThemeMenuAnchor(null);
  };

  const handleThemeSelect = (themeKey) => {
    setColorMode(themeKey);
    handleThemeMenuClose();
  };

  const { canViewSupervisor } = useMemo(() => {
    const normalizedRole = (user?.role || "").toLowerCase();
    return {
      canViewSupervisor: normalizedRole === "admin" || normalizedRole === "supervisor",
    };
  }, [user?.role]);

  const brandLogo = useMemo(() => {
    // Use light logo only for light mode, dark logo for all dark-based themes
    return mode === "light" ? "darkstarenterpriseday.png" : "darkstarenterprise.png";
  }, [mode]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const stampLastActive = () => {
      updateProfile((prev) => {
        const nextStamp = new Date().toISOString();
        if (prev.lastActiveAt === nextStamp) {
          return prev;
        }
        return { ...prev, lastActiveAt: nextStamp };
      });
    };

    stampLastActive();
    const handleVisibility = () => {
      if (!document.hidden) {
        stampLastActive();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [location.pathname]);

  const [pingMs, setPingMs] = useState(null);
  const [pingFailed, setPingFailed] = useState(false);
  const pingIntervalRef = useRef(DEFAULT_PING_INTERVAL_SECONDS);

  const pingServer = useCallback(async () => {
    if (!user) {
      setPingMs(null);
      setPingFailed(false);
      pingIntervalRef.current = DEFAULT_PING_INTERVAL_SECONDS;
      return;
    }

    const start = typeof performance !== "undefined" ? performance.now() : Date.now();

    try {
      const response = await api.get("/status/ping");
      const end = typeof performance !== "undefined" ? performance.now() : Date.now();
      const latency = Math.max(0, Math.round(end - start));
      setPingMs(latency);
      setPingFailed(false);

      const recommended = Number(response.data?.recommendedIntervalSeconds);
      if (
        Number.isFinite(recommended) &&
        recommended >= MIN_PING_INTERVAL_SECONDS &&
        recommended <= MAX_PING_INTERVAL_SECONDS
      ) {
        pingIntervalRef.current = recommended;
      }
    } catch (error) {
      console.error("Ping failed", error);
      setPingFailed(true);
      setPingMs(null);
    }
  }, [user]);

  useEffect(() => {
    if (!user || typeof window === "undefined") {
      setPingMs(null);
      setPingFailed(false);
      pingIntervalRef.current = DEFAULT_PING_INTERVAL_SECONDS;
      return undefined;
    }

    let cancelled = false;
    let timeoutId;

    const tick = async () => {
      if (cancelled) {
        return;
      }

      await pingServer();

      if (cancelled) {
        return;
      }

      const delaySeconds = Math.min(
        MAX_PING_INTERVAL_SECONDS,
        Math.max(MIN_PING_INTERVAL_SECONDS, pingIntervalRef.current || DEFAULT_PING_INTERVAL_SECONDS)
      );

      timeoutId = window.setTimeout(tick, delaySeconds * 1000);
    };

    tick();

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [user, pingServer]);

  const pingLabel = pingFailed ? "NO LINK" : pingMs === null ? "PING..." : `${pingMs} ms`;

  const resolvePingColor = () => {
    if (pingFailed) {
      return "error.main";
    }
    if (pingMs === null) {
      return "grey.500";
    }
    if (pingMs < 200) {
      return "success.main";
    }
    if (pingMs <= 500) {
      return "warning.main";
    }
    return "error.main";
  };

  const renderAnnouncement = () => {
    if (!announcement?.message) {
      return null;
    }

    const paletteKey = announcement.type === "urgent" ? "error" : announcement.type === "warning" ? "warning" : "success";
    const showBeacons = announcement.beaconsEnabled;

    // DMS (Dynamic Message Sign) style when beacons enabled
    if (showBeacons) {
      return (
        <Box
          onClick={toggleSnowbank}
          sx={{
            ml: { xs: 0, sm: 2 },
            mt: { xs: 1, sm: 0 },
            borderRadius: 1,
            backgroundColor: "#0a0a0a",
            border: snowbankEnabled ? "3px solid #4fc3f7" : "3px solid #333",
            boxShadow: snowbankEnabled
              ? "inset 0 0 10px rgba(0,0,0,0.8), 0 2px 8px rgba(0,0,0,0.5), 0 0 15px rgba(79, 195, 247, 0.5)"
              : "inset 0 0 10px rgba(0,0,0,0.8), 0 2px 8px rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            p: 1,
            maxWidth: { xs: "100%", sm: 620 },
            flexShrink: 0,
            cursor: "pointer",
            transition: "border-color 0.3s, box-shadow 0.3s",
            "&:hover": {
              borderColor: snowbankEnabled ? "#81d4fa" : "#555",
            },
            // Global keyframes defined once at parent level
            "@keyframes beacon-left": {
              "0%, 49%": {
                opacity: 1,
                boxShadow: "0 0 12px 6px rgba(255, 179, 0, 0.9)",
              },
              "50%, 100%": {
                opacity: 0.2,
                boxShadow: "0 0 2px 1px rgba(255, 179, 0, 0.2)",
              },
            },
            "@keyframes beacon-right": {
              "0%, 49%": {
                opacity: 0.2,
                boxShadow: "0 0 2px 1px rgba(255, 179, 0, 0.2)",
              },
              "50%, 100%": {
                opacity: 1,
                boxShadow: "0 0 12px 6px rgba(255, 179, 0, 0.9)",
              },
            },
          }}
        >
          {/* Left Beacon */}
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: "#1a1a1a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mx: 1.5,
              border: "2px solid #333",
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                backgroundColor: "#ffb300",
                animation: "beacon-left 1s ease-in-out infinite",
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              color: "#ffb300",
              fontWeight: 700,
              fontFamily: "'Courier New', monospace",
              fontSize: 13,
              letterSpacing: 1,
              textTransform: "uppercase",
              textShadow: "0 0 8px rgba(255, 179, 0, 0.7), 0 0 2px rgba(255, 179, 0, 0.9)",
              textAlign: "center",
              lineHeight: 1.4,
              wordBreak: "break-word",
            }}
          >
            {announcement.message}
          </Box>
          {/* Right Beacon */}
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: "#1a1a1a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mx: 1.5,
              border: "2px solid #333",
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                backgroundColor: "#ffb300",
                animation: "beacon-right 1s ease-in-out infinite",
              }}
            />
          </Box>
        </Box>
      );
    }

    // Normal announcement style
    return (
      <Box
        sx={{
          ml: { xs: 0, sm: 2 },
          mt: { xs: 1, sm: 0 },
          px: 1.5,
          py: 0.75,
          borderRadius: 1.5,
          backgroundColor: `${paletteKey}.main`,
          color: `${paletteKey}.contrastText`,
          fontWeight: 600,
          letterSpacing: 0.4,
          textTransform: "none",
          fontSize: 12,
          maxWidth: { xs: "100%", sm: 520 },
          flexShrink: 0,
          lineHeight: 1.35,
          wordBreak: "break-word",
          boxShadow: 1,
        }}
      >
        {announcement.message}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", "--cad-header-height": `${headerHeight}px`, "--cad-navigation-width": `${currentDrawerWidth}px` }}>
      <Drawer
        variant={compactNavigation ? "temporary" : "permanent"}
        open={navigationOpen}
        onClose={() => setMobileNavigationOpen(false)}
        PaperProps={{ id: "cad-main-navigation", component: "nav", "aria-label": "Main navigation", "aria-hidden": !navigationOpen ? true : undefined }}
        sx={{
          width: currentDrawerWidth,
          flexShrink: 0,
          transition: 'width 0.2s ease-in-out',
          [`& .MuiDrawer-paper`]: {
            width: compactNavigation ? drawerWidth : currentDrawerWidth,
            maxWidth: "calc(100vw - 32px)",
            visibility: navigationOpen ? "visible" : "hidden",
            overscrollBehavior: "contain",
            pt: "env(safe-area-inset-top, 0px)",
            pb: "env(safe-area-inset-bottom, 0px)",
            pl: "env(safe-area-inset-left, 0px)",
            "& .MuiListItemButton-root": { minHeight: 44 },
            "& .MuiListItemText-primary": { overflowWrap: "anywhere" },
            boxSizing: "border-box",
            transition: 'width 0.2s ease-in-out',
            overflowX: 'hidden',
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          onClick={event => {
            if (compactNavigation && event.target.closest?.("a[href]")) setMobileNavigationOpen(false);
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between", gap: 1, borderBottom: 1, borderColor: 'divider', mb: 1 }}>
            <Box sx={{ py: 2 }}>
              <Typography variant="subtitle2" fontWeight={800} letterSpacing={1.5}>RANGER VAULT</Typography>
              <Typography variant="caption" color="text.secondary">Texas Highway Rangers</Typography>
            </Box>
            {compactNavigation && (
              <IconButton aria-label="Close navigation" onClick={() => setMobileNavigationOpen(false)} sx={{ minHeight: 44, minWidth: 44 }}>
                <ChevronLeftIcon />
              </IconButton>
            )}
          </Toolbar>
          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            <List>
              <ListItemButton component={NavLink} to="/dashboard" sx={navButtonStyles}>
                <ListItemText primary="Incident Management" />
              </ListItemButton>

              <ListItemButton component={NavLink} to="/my-vault" sx={navButtonStyles}>
                <ListItemText primary="MyVault" />
              </ListItemButton>
              <List disablePadding>
                <ListItemButton component={NavLink} to="/my-vault/resources" sx={nestedNavStyles}>
                  <ListItemText primary="Resources" />
                </ListItemButton>
                <ListItemButton component={NavLink} to="/my-vault/training" sx={nestedNavStyles}>
                  <ListItemText primary="Training" />
                </ListItemButton>
                <ListItemButton component={NavLink} to="/my-vault/profile" sx={nestedNavStyles}>
                  <ListItemText primary="Profile" />
                </ListItemButton>
              </List>

              {canViewSupervisor && (
                <>
                  <ListItemButton component={NavLink} to="/supervisor" sx={navButtonStyles}>
                    <ListItemText primary="Supervisor" />
                  </ListItemButton>
                  <ListItemButton component={NavLink} to="/supervisor/history" sx={nestedNavStyles}>
                    <ListItemText primary="Incident History" />
                  </ListItemButton>
                  <ListItemButton component={NavLink} to="/supervisor/analytics" sx={nestedNavStyles}>
                    <ListItemText primary="Analytics" />
                  </ListItemButton>

                </>
              )}

              <ListItemButton component={NavLink} to="/settings" sx={navButtonStyles}>
                <ListItemText primary="Settings" />
              </ListItemButton>
            </List>
          </Box>

          <Box sx={{ px: 2, pb: 2 }}>
            <Box
              component="a"
              href="https://darkstarenterprise.co"
              target="_blank"
              rel="noreferrer noopener"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                textDecoration: "none",
                textTransform: "uppercase",
                letterSpacing: 2,
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 2,
                px: 2,
                py: 1.5,
                backgroundColor: mode === "light" ? "rgba(30,60,114,0.08)" : "rgba(5,5,5,0.95)",
                color: mode === "light" ? "rgba(30,60,114,0.95)" : "rgba(255,255,255,0.95)",
                border: (theme) => `1px solid ${mode === "light" ? "rgba(30,60,114,0.15)" : "rgba(255,255,255,0.2)"}`,
                boxShadow:
                  mode === "light"
                    ? "0 6px 16px rgba(30,60,114,0.18)"
                    : "0 12px 32px rgba(0,0,0,0.8)",
              }}
            >
              <Box component="span">Created by</Box>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/${brandLogo}`}
                alt="Darkstar Enterprise"
                sx={{ width: 160, height: "auto", objectFit: "contain" }}
              />
            </Box>
          </Box>

        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
        <AppBar ref={headerRef} position="sticky" elevation={0} sx={{ pt: "env(safe-area-inset-top, 0px)", bgcolor: 'background.paper', color: 'text.primary', borderBottom: 1, borderColor: 'divider' }}>
          <Toolbar sx={{
            flexWrap: "wrap", columnGap: 1, rowGap: 1, py: 1,
            pl: { xs: "calc(16px + env(safe-area-inset-left, 0px))", sm: "calc(24px + env(safe-area-inset-left, 0px))" },
            pr: { xs: "calc(16px + env(safe-area-inset-right, 0px))", sm: "calc(24px + env(safe-area-inset-right, 0px))" },
          }}>
            <Tooltip title={navigationLabel}>
              <IconButton
                color="inherit"
                onClick={toggleSidebar}
                aria-label={navigationLabel}
                aria-controls="cad-main-navigation"
                aria-expanded={navigationOpen}
                edge="start"
                size={tabletMode ? "large" : "medium"}
                sx={{ mr: { xs: 0, md: 1 }, minHeight: 44, minWidth: 44 }}
              >
                {!navigationOpen ? <MenuIcon fontSize={tabletMode ? "large" : "medium"} /> : <ChevronLeftIcon fontSize={tabletMode ? "large" : "medium"} />}
              </IconButton>
            </Tooltip>
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", sm: "center" },
                flexGrow: 1,
                minWidth: 0,
                overflowWrap: "anywhere",
                flexWrap: "wrap",
                columnGap: 1.5,
                rowGap: 1,
              }}
            >
              {showHeaderLogo && (
                <Box
                  component="img"
                  src={pageMeta.logo}
                  alt=""
                  aria-hidden="true"
                  sx={{ width: 44, height: 34, objectFit: "contain", mr: 1 }}
                />
              )}
              <Typography variant="h6" sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                {pageMeta.label}
              </Typography>
              <RotatingQuote hidden={!!announcement?.message} />
              {renderAnnouncement()}
            </Box>

            <Stack direction="row" spacing={tabletMode ? 2 : 1.5} useFlexGap flexWrap="wrap" alignItems="center" sx={{ minWidth: 0, maxWidth: "100%" }}>
              <MessagesButton/>
              {toolbarExtras}
              <WorkspaceTools {...workspaceToolsProps} />

              <Tooltip title="Change theme">
                <IconButton color="inherit" aria-label="Change theme" onClick={handleThemeMenuOpen} size={tabletMode ? "medium" : "small"} sx={{ minHeight: 44, minWidth: 44, ...(tabletMode ? { p: 1.25 } : {}) }}>
                  <PaletteIcon fontSize={tabletMode ? "medium" : "small"} />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={themeMenuAnchor}
                open={themeMenuOpen}
                onClose={handleThemeMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {Object.entries(THEME_MODES).map(([key, { label }]) => (
                  <MenuItem
                    key={key}
                    onClick={() => handleThemeSelect(key)}
                    selected={mode === key}
                    sx={{
                      ...(tabletMode && { py: 1.5, fontSize: "1.05rem" }),
                      ...(key === "greenBlack" && {
                        color: "#00ff00",
                        "&:hover": { backgroundColor: "rgba(0, 255, 0, 0.1)" },
                      }),
                      ...(key === "redBlack" && {
                        color: "#ff3333",
                        "&:hover": { backgroundColor: "rgba(255, 51, 51, 0.1)" },
                      }),
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {mode === key ? <CheckIcon fontSize="small" color="primary" /> : null}
                    </ListItemIcon>
                    {label}
                  </MenuItem>
                ))}
              </Menu>

              <Button
                color="inherit"
                variant="outlined"
                size={tabletMode ? "medium" : "small"}
                onClick={logout}
                startIcon={<LogoutIcon fontSize={tabletMode ? "medium" : "small"} />}
                sx={{ minHeight: 44, ...(tabletMode ? { fontSize: "1rem", px: 2.5, py: 1 } : {}) }}
              >
                Log Out
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>

        <Box sx={{
          p: { xs: 2, sm: 3 },
          pl: { xs: "calc(16px + env(safe-area-inset-left, 0px))", sm: "calc(24px + env(safe-area-inset-left, 0px))" },
          pr: { xs: "calc(16px + env(safe-area-inset-right, 0px))", sm: "calc(24px + env(safe-area-inset-right, 0px))" },
          pb: "max(24px, env(safe-area-inset-bottom, 0px))",
        }}>
          {title && typeof title !== "string" && <Box sx={{ mb: 3 }}>{title}</Box>}
          {children}
          <Typography variant="caption" component="div" sx={{ mt: 2, textAlign: 'right', color: 'text.secondary' }}>
            Location data: <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap contributors</a>
          </Typography>
        </Box>

        {/* Warning modal for users with too many incomplete incidents */}
        <IncompleteIncidentsWarning />

      </Box>
    </Box>
  );
}

export default DashboardLayout;
